import React, { useState } from 'react';
import { CheckCircle2, FileText, Building2, Users, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { natsInfo, napsInfo, tpaRole, establishmentRequirements, partnerLogos } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApprenticeshipPage = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    location: '',
    workforce_size: '',
    scheme: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.workforce_size || !formData.scheme) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/establishments`, formData);
      
      toast.success('Registration submitted successfully!', {
        description: 'Our team will contact you within 24 hours.'
      });
      
      setFormData({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        location: '',
        workforce_size: '',
        scheme: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting establishment form:', error);
      toast.error('Failed to submit registration', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-700 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Apprenticeship Programs</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Transform Your Workforce with Apprenticeship
            </h1>
            <p className="text-xl text-blue-200 leading-relaxed">
              Partner with Park Skill to implement NATS & NAPS programs and access skilled manpower with government support.
            </p>
          </div>
        </div>
      </section>

      {/* NATS Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={partnerLogos.nats}
                alt="NATS"
                className="w-48 mb-6 bg-white p-4 rounded-xl shadow"
              />
              <h2 className="text-3xl font-bold text-slate-900 mb-2">{natsInfo.title}</h2>
              <p className="text-emerald-600 font-medium mb-4">{natsInfo.ministry}</p>
              <p className="text-slate-600 leading-relaxed mb-6">{natsInfo.description}</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    Benefits
                  </h3>
                  <ul className="space-y-2">
                    {natsInfo.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-700 mt-2" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-700" />
                    Eligibility
                  </h3>
                  <ul className="space-y-2">
                    {natsInfo.eligibility.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-8">
              <img
                src="https://images.unsplash.com/photo-1758522275031-70ea3febfa2e?w=600&q=80"
                alt="NATS Training"
                className="w-full h-64 object-cover rounded-xl mb-4"
              />
              <div className="text-center">
                <p className="text-slate-600">Graduate & Diploma Apprenticeship Training Program</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NAPS Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-white rounded-2xl p-8">
              <img
                src="https://images.unsplash.com/photo-1759523146335-0069847ceb16?w=600&q=80"
                alt="NAPS Training"
                className="w-full h-64 object-cover rounded-xl mb-4"
              />
              <div className="text-center">
                <p className="text-slate-600">On-the-Job Skills Training for All Sectors</p>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <img
                src={partnerLogos.naps}
                alt="NAPS"
                className="w-48 mb-6 bg-white p-4 rounded-xl shadow"
              />
              <h2 className="text-3xl font-bold text-slate-900 mb-2">{napsInfo.title}</h2>
              <p className="text-emerald-600 font-medium mb-4">{napsInfo.ministry}</p>
              <p className="text-slate-600 leading-relaxed mb-6">{napsInfo.description}</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    Benefits
                  </h3>
                  <ul className="space-y-2">
                    {napsInfo.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-700 mt-2" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-700" />
                    Eligibility
                  </h3>
                  <ul className="space-y-2">
                    {napsInfo.eligibility.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TPA Role Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-blue-700 to-slate-900 rounded-3xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <Users className="w-12 h-12 text-emerald-400 mb-4" />
                <h2 className="text-3xl font-bold text-white mb-4">{tpaRole.title}</h2>
                <p className="text-blue-200 leading-relaxed mb-6">{tpaRole.description}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4">Our TPA Services</h3>
                <ul className="space-y-3">
                  {tpaRole.services.map((service, i) => (
                    <li key={i} className="flex items-center gap-3 text-blue-100">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements & Registration */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Requirements */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">{establishmentRequirements.title}</h2>
              
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-700" />
                    Required Documents
                  </h3>
                  <ul className="space-y-2">
                    {establishmentRequirements.documents.map((doc, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-700 mt-2" />
                        {doc}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    Benefits of Partnership
                  </h3>
                  <ul className="space-y-2">
                    {establishmentRequirements.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Registration Form */}
            <div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Register Your Establishment</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="Enter company name"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact_person">Contact Person *</Label>
                      <Input
                        id="contact_person"
                        name="contact_person"
                        value={formData.contact_person}
                        onChange={handleInputChange}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Mobile number"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="company@example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="City, State"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="workforce_size">Workforce Size *</Label>
                      <Select onValueChange={(value) => handleSelectChange('workforce_size', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4-30">4-30 employees</SelectItem>
                          <SelectItem value="30-100">30-100 employees</SelectItem>
                          <SelectItem value="100-500">100-500 employees</SelectItem>
                          <SelectItem value="500+">500+ employees</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="scheme">Interested Scheme *</Label>
                      <Select onValueChange={(value) => handleSelectChange('scheme', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select scheme" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nats">NATS</SelectItem>
                          <SelectItem value="naps">NAPS</SelectItem>
                          <SelectItem value="both">Both NATS & NAPS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Additional Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your requirements..."
                      rows={4}
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>
                
                <p className="text-xs text-slate-500 mt-4 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  Our team will contact you within 24 hours to discuss partnership details.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Partners Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Network</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Industry Partners
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Collaborating with 37+ establishments across India for apprenticeship programs under NATS & NAPS.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-4">
            <img
              src={partnerLogos.industryPartnersAlt}
              alt="Industry Partners - Apprenticeship Collaborations"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ApprenticeshipPage;
